import React, { StrictMode, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Simple Error Boundary Component with Retry
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by Error Boundary:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 text-center p-4">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4">
            Something went wrong. Please try again.
          </h1>
          <button
            onClick={this.handleRetry}
            className="bg-accent text-white px-4 py-2 rounded-lg hover:bg-accent/80 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Retry loading the application"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(
    {
      resources: {
        en: {
          translation: {
            header: {
              home: "Home",
              about: "About",
              ministries: "Ministries",
              events: "Events",
              donate: "Donate Now",
              contact: "Contact",
              prayerRequest: "Prayer Request",
              testimonials: "Testimonials",
            },
            home: {
              seoTitle: "NAOGM - Spreading the Gospel Worldwide",
              seoDescription: "Not Ashamed of the Gospel Ministry is dedicated to spreading the gospel of Jesus Christ to all nations through crusades, outreaches, and community service.",
              heroTitle: "Not Ashamed of the Gospel Ministry",
              heroSubtitle: "Spreading the Gospel of Jesus Christ to All Nations (Romans 1:16)",
              donateButton: "Donate Now",
              impactTitle: "Our Impact",
              impactPeople: "People Reached",
              impactCrusades: "Crusades Held",
              impactCountries: "Countries Served",
              welcomeTitle: "Welcome to NAOGM",
              welcomeText:
                "Guided by the Great Commission (Matthew 28:19-20), we are committed to evangelizing, discipling, and equipping believers to live boldly for Christ through crusades, outreaches, and community service.",
              visionTitle: "Our Vision",
              visionText:
                "To spread the gospel of Jesus Christ to all nations and make committed disciples, not just converts.",
              welcomeButton: "Learn More",
              featuredEventTitle: "Featured Event",
              eventTitle: "Set Apart Virtual Conference. Episode 3",
              eventDetails: "Aug 30th, 2026 5:00 PM EAT | 4:00 PM CAT , Live on our YouTube Channel.",
              eventButton: "Join Now",
              eventLinkUrl: "https://meet.google.com/rvi-xaoc-ozj",
              testimonialsTitle: "What People Are Saying",
              testimonial1: "NAOGM’s crusades brought me closer to Christ and transformed my life.",
              testimonial2: "The discipleship program equipped me to share my faith boldly.",
              newsletterTitle: "Stay Connected",
              newsletterText: "Sign up for our newsletter to receive updates on our crusades, outreaches, and more.",
              newsletterPlaceholder: "Enter your email",
              newsletterButton: "Subscribe",
              newsletterSuccess: "Thank you for subscribing!",
              newsletterEmailRequired: "Email is required",
              newsletterEmailInvalid: "Please enter a valid email address",
              newsletterSubmitting: "Submitting...",
              newsletterError: "There was an error submitting your subscription. Please try again.",
              newsletterPrivacyNotice: "Your data will be submitted via UseBasin. See their",
              newsletterPrivacyLink: "Privacy Policy",
              ctaTitle: "Join Our Mission",
              ctaEvents: "Upcoming Events",
              ctaDonate: "Support Us",
              ctaPrayer: "Submit a Prayer Request",
            },
            about: {
              seoTitle: "About NAOGM - Our Mission and History",
              seoDescription: "Learn about Not Ashamed of the Gospel Ministry, our mission, vision, leadership, and statement of faith.",

              heroTitle: "About NAOGM",
              heroSubtitle: "Discover Our Mission, History, and Leadership",

              historyTitle: "Our History",
              historyText1: "Not Ashamed of the Gospel Ministry (NAOGM) was founded in 2021 with a vision to fulfill the Great Commission by spreading the gospel to all nations.",
              historyText2: "Since then, we have held over 5 crusades, reached thousands of people, and established discipleship programs to equip believers for ministry.",

              missionTitle: "Our Mission",
              missionText: "Guided by the Great Commission (Matthew 28:19-20), we are committed to evangelizing, discipling, and equipping believers to live boldly for Christ through crusades, outreaches, and community service.",

              visionTitle: "Our Vision",
              visionText: "To spread the gospel of Jesus Christ to all nations and make committed disciples, not just converts.",

              leadershipTitle: "Meet Our Leadership",

              leader1Name: "Pastor Akilimali",
              leader1Role: "Founder & Senior Pastor",
              leader1Bio: "Pastor Akilimali has been leading NAOGM since its inception, with a passion for evangelism and discipleship.",

              leader2Name: "Baluka Mercy",
              leader2Role: "Director of Outreach",
              leader2Bio: "Mercy oversees NAOGM’s outreach programs, ensuring the gospel reaches remote communities.",

              leader3Name: "Simi",
              leader3Role: "Discipleship Leader",
              leader3Bio: "Simi is dedicated to equipping young believers to live out their faith boldly.",

              leader4Name: "Tindyebwa Jovia",
              leader4Role: "Finance Lead",
              leader4Bio: "Oversees financial planning, budgeting, and accountability within the ministry.",

              leader5Name: "Gibemi Musa Lumumba",
              leader5Role: "Communications Lead",
              leader5Bio: "Leads communication strategy, branding, and outreach initiatives.",

              faithTitle: "Statement of Faith",
              faithScripture: "“For I am not ashamed of the gospel, for it is the power of God for salvation to everyone who believes.” – Romans 1:16",

              faithPoint1: "We believe in the Holy Trinity: Father, Son, and Holy Spirit.",
              faithPoint2: "We believe the Bible is the inspired and infallible Word of God.",
              faithPoint3: "We believe Jesus Christ is the Son of God, who died for our sins and rose again.",
              faithPoint4: "We believe in the Great Commission to make disciples of all nations.",

              ctaTitle: "Get in Touch",
              ctaButton: "Contact Us"
            },
            ministries: {
              seoTitle: "NAOGM Ministries - Our Work",
              seoDescription: "Explore the ministries of Not Ashamed of the Gospel Ministry, including crusades, discipleship, youth programs, and community outreach.",
              heroTitle: "Our Ministries",
              heroSubtitle: "Discover How We Serve and Spread the Gospel",
              sectionTitle: "Our Core Ministries",
              crusadesTitle: "Crusades",
              crusadesDescription: "We organize large-scale crusades to share the gospel with thousands, bringing hope and salvation to communities.",
              crusadesLink: "View Upcoming Crusades",
              discipleshipTitle: "Discipleship Programs",
              discipleshipDescription: "Our programs equip believers to grow in faith, share the gospel, and live out their calling as disciples of Christ.",
              discipleshipLink: "Learn More",
              youthTitle: "Youth Ministry",
              youthDescription: "We empower young people to live boldly for Christ through mentorship, events, and discipleship tailored for youth.",
              youthLink: "Get Involved",
              outreachTitle: "Community Outreach",
              outreachDescription: "We serve communities through humanitarian efforts, sharing the love of Christ through practical acts of service.",
              outreachLink: "Support Our Outreach",
              ctaTitle: "Join Our Mission",
              ctaButton: "Contact Us",
            },
            events: {
              seoTitle: "NAOGM Events - Upcoming Prayer Conferences",
              seoDescription: "Join us for upcoming virtual prayer conferences and outreaches organized by Not Ashamed of the Gospel Ministry.",
              heroTitle: "Upcoming Events",
              heroSubtitle: "Join Us in Spreading the Gospel and Praying for Transformation",
              featuredSectionTitle: "Featured Event",
              featuredTitle: "SET APART VIRTUAL CONFERENCE",
              featuredDate: "Just like last year,  this conferences will run June - December 2026",
              featuredLocation: "The event will be live on our YouTube channel (NAOGM) on 28th June",
              featuredDescription: "Join us for the second edition of our virtual prayer conference, birthed in July 2025. Through fervent prayer, worship, and the Word, we intercede for families, relationships, marriages, careers, and soul winning, believing God to break barriers, heal wounds, restore destinies, and enable us to leap over every wall (Psalm 18:29). Expect Spirit-led worship, scriptural insights, targeted prayers, and prophetic encouragement.",
              featuredLink: "Join Now",
              featuredLinkUrl: "https://www.youtube.com/@NAOGM21",
              upcomingSectionTitle: "More Upcoming Events",
              event1Title: "THE CALL SERIES Season 1 - 2026",
              event1Date: "",
              event3Title: "SET APART VIRTUAL CONFERENCE 2026",
              event3Date: "August 30, 2026",
              event1Location: "Virtual (YouTube Live)",
              event3Location: "Virtual (YouTube Live)",
              event1Description: "The call Series Season 1 - 2026. Let's get ready soon and invite our friends, let's reach out and be a blessing to our friends and families.",
              event3Description: "As they ministered to the Lord and fasted, the Holy Spirit said, 'Set apart for Me Barnabas and Saul for the work to which I have called them, Acts 13:2.'",
              event1Link: "Learn More",
              event2Title: "2025 In Review",
              event2Date: "January, 2026",
              event2Location: "Goma, DRC",
              event2Description: "Encounter Christ, grow in faith, and connect with other young believers.",
              event2Link: "Register Now",
              event3Link: "Join Now",
              ctaTitle: "Want to Host an Event?",
              ctaButton: "Contact Us",
            },
            contact: {
              seoTitle: "Contact NAOGM - Get in Touch",
              seoDescription: "Contact Not Ashamed of the Gospel Ministry for inquiries, partnerships, or to join our mission.",
              heroTitle: "Contact Us",
              heroSubtitle: "We’d Love to Hear from You",
              detailsTitle: "Get in Touch",
              detailsSubtitle: "Reach out to NAOGM for inquiries, partnership opportunities, or to join our mission.",
              addressLabel: "Headquarters",
              address: "Goma, North-Kivu (DRC)",
              emailLabel: "Email",
              email: "info@notashamedofthegospelministry.org",
              phoneLabel: "Phone",
              phone: "+243-857-274-262",
              mapTitle: "Find Us on the Map",
            },
            donate: {
              seoTitle: "Donate to NAOGM - Support Our Mission",
              seoDescription: "Support Not Ashamed of the Gospel Ministry by donating to help spread the gospel and serve communities worldwide.",
              heroTitle: "Support Our Mission",
              heroSubtitle: "Your Contribution Makes a Difference",
              sectionTitle: "Ways to Give",
              sectionSubtitle: "Your generous contributions help us spread the gospel, conduct crusades, and support communities worldwide.",
              oneTimeTitle: "One-Time Donation",
              oneTimeDescription: "Make a one-time gift to support our evangelistic crusades and outreach programs.",
              oneTimeButton: "Donate Now",
              monthlyTitle: "Monthly Giving",
              monthlyDescription: "Become a monthly partner to sustain our discipleship and training programs.",
              monthlyButton: "Join Monthly",
              projectTitle: "Project Sponsorship",
              projectDescription: "Support specific initiatives like medical missions or community welfare programs.",
              projectButton: "Sponsor a Project",
              bankTransferTitle: "Donate via Bank Transfer",
              bankLabel: "Bank:",
              bankName: "[Coming Soon]",
              accountNameLabel: "Account Name:",
              accountName: "coming soon",
              accountNumberLabel: "Account Number:",
              accountNumber: "[Coming Soon]",
              swiftCodeLabel: "Swift Code:",
              swiftCode: "[Coming Soon]",
            },
            testimonials: {
              seoTitle: "NAOGM Testimonials - Stories of Impact",
              seoDescription: "Hear from individuals whose lives have been transformed by Not Ashamed of the Gospel Ministry’s outreach efforts.",
              heroTitle: "Testimonials",
              heroSubtitle: "Stories of Lives Transformed",
              sectionTitle: "What People Are Saying",
              sectionSubtitle: "Hear from those impacted by NAOGM’s ministry and outreach efforts.",
              testimonial1: {
                quote: "NAOGM’s crusades brought me closer to Christ and transformed my life.",
                author: "John",
                location: "Kampala, Uganda",
              },
              testimonial2: {
                quote: "The discipleship program equipped me to share my faith boldly.",
                author: "Micheal",
                location: "Nairobi, Kenya",
              },
              testimonial3: {
                quote: "The youth revival camp was a turning point for me. I found purpose in Christ.",
                author: "Okello",
                location: "Dar es Salaam, Tanzania",
              },
              testimonial4: {
                quote: "Through NAOGM’s outreach, my community received hope and practical support.",
                author: "Esther",
                location: "Kigali, Rwanda",
              },
              ctaTitle: "Have a Story to Share?",
              ctaButton: "Contact Us",
            },
            prayerRequest: {
              seoTitle: "NAOGM Prayer Request - Submit Your Prayer",
              seoDescription: "Submit your prayer request to Not Ashamed of the Gospel Ministry, and our team will pray with you.",
              heroTitle: "Prayer Request",
              heroSubtitle: "Let Us Pray With You",
              sectionTitle: "Submit a Prayer Request",
              sectionSubtitle: "We believe in the power of prayer. Share your prayer requests, and our team will pray with you.",
              successMessage: "Thank you for submitting your prayer request! Our team will pray with you.",
              form: {
                nameLabel: "Name",
                namePlaceholder: "Enter your name",
                nameRequired: "Name is required",
                emailLabel: "Email",
                emailPlaceholder: "Enter your email",
                emailRequired: "Email is required",
                emailInvalid: "Please enter a valid email address",
                requestLabel: "Prayer Request",
                requestPlaceholder: "Share your prayer request",
                requestRequired: "Prayer request is required",
                anonymousLabel: "Submit anonymously",
                submitButton: "Submit Request",
                submitting: "Submitting...",
                errorMessage: "There was an error submitting your request. Please try again.",
                privacyNotice: "Your data will be submitted. Fill details below",
                privacyLink: "Privacy Policy",
              },
            },
            footer: {
              aboutTitle: "Not Ashamed of the Gospel Ministry",
              headquartersLabel: "Headquarters",
              emailLabel: "Email",
              phoneLabel: "Phone",
              linksTitle: "Quick Links",
              privacyPolicy: "Privacy Policy",
              termsOfService: "Terms of Service",
              socialTitle: "Connect With Us",
              socialText: "Follow us on social media for updates on our crusades and outreaches.",
              facebookLabel: "Visit us on Facebook",
              twitterLabel: "Follow us on Twitter",
              instagramLabel: "Follow us on Instagram",
              rightsReserved: "All Rights Reserved.",
              poweredBy: "Site is powered by",
            },
          },
        },
        sw: {
          translation: {
            header: {
              home: "Nyumbani",
              about: "Kuhusu",
              ministries: "Huduma",
              events: "Matukio",
              donate: "Changia Sasa",
              contact: "Wasiliana",
              prayerRequest: "Ombi la Maombi",
              testimonials: "Ushuhuda",
            },
            home: {
              seoTitle: "NAOGM - Kueneza Injili Ulimwenguni",
              seoDescription: "Huduma ya Injili ya Kusikatazwa (NAOGM) imejitolea kueneza Injili ya Yesu Kristo kwa mataifa yote kupitia mikutano ya kuhubiri, huduma za kijamii na uanafunzi.",
              heroTitle: "Huduma ya Injili ya Kusikatazwa",
              heroSubtitle: "Kueneza Injili ya Yesu Kristo kwa Mataifa Yote (Warumi 1:16)",
              donateButton: "Changia Sasa",
              impactTitle: "Athari Zetu",
              impactPeople: "Watu Waliovutwa",
              impactCrusades: "Mikutano ya Kuhubiri Iliyofanyika",
              impactCountries: "Nchi Zilizohudumiwa",
              welcomeTitle: "Karibu NAOGM",
              welcomeText: "Tukiongozwa na Agizo Kuu (Mathayo 28:19-20), tumejitolea kuhubiri Injili, kuwafundisha na kuwatayarisha waumini kuishi kwa ujasiri kwa Kristo kupitia mikutano ya kuhubiri, huduma za kijamii na uanafunzi.",
              visionTitle: "Maono Yetu",
              visionText: "Kueneza Injili ya Yesu Kristo kwa mataifa yote na kuwafanya wanafunzi waliojitolea, si waongofu tu.",
              welcomeButton: "Jifunze Zaidi",
              featuredEventTitle: "Tukio Lililoangaziwa",
              eventTitle: "Kubadilisha Mifumo ya Maisha – Kipindi cha 2: Kuruka Juu ya Kuta",
              eventDetails: "Jiunge nasi katika mkutano wa maombi mtandaoni kila Jumapili ya mwisho kuanzia Julai hadi Desemba 2025, saa 3:00 usiku hadi saa 6:00 usiku (CAT), kupitia Google Meet. Tarajia ibada yenye nguvu, maombi na mafundisho kutoka Zaburi 18:29.",
              eventButton: "Jiunge Sasa",
              eventLinkUrl: "https://meet.google.com/rvi-xaoc-ozj",
              testimonialsTitle: "Watu Wanasema Nini",
              testimonial1: "Mikutano ya NAOGM ilinikaribisha karibu na Kristo na kubadilisha maisha yangu.",
              testimonial2: "Programu ya uanafunzi ilinitayarisha kushiriki imani yangu kwa ujasiri.",
              newsletterTitle: "Baki Ukiunganishwa",
              newsletterText: "Jiandikishe katika jarida letu ili upate sasisho kuhusu mikutano yetu, huduma za kijamii na mengineyo.",
              newsletterPlaceholder: "Ingiza barua pepe yako",
              newsletterButton: "Jiandikishe",
              newsletterSuccess: "Asante kwa kujiandikisha!",
              newsletterEmailRequired: "Barua pepe inahitajika",
              newsletterEmailInvalid: "Tafadhali ingiza barua pepe halali",
              newsletterSubmitting: "Inawasilishwa...",
              newsletterError: "Kulikuwa na hitilafu katika kuwasilisha usajili wako. Tafadhali jaribu tena.",
              newsletterPrivacyNotice: "Data yako itawasilishwa kupitia UseBasin. Angalia",
              newsletterPrivacyLink: "Sera ya Faragha",
              ctaTitle: "Jiunge na Dhamira Yetu",
              ctaEvents: "Matukio Yanayokuja",
              ctaDonate: "Tuunge Mkono",
              ctaPrayer: "Wasilisha Ombi la Maombi",
            },
            about: {
              seoTitle: "Kuhusu NAOGM - Dhamira na Historia Yetu",
              seoDescription: "Jifunze kuhusu Huduma ya Injili ya Kusikatazwa (NAOGM), dhamira yetu, maono, uongozi na kauli ya imani.",
              heroTitle: "Kuhusu NAOGM",
              heroSubtitle: "Gundua Dhamira Yetu, Historia na Uongozi",
              historyTitle: "Historia Yetu",
              historyText1: "Huduma ya Injili ya Kusikatazwa (NAOGM) ilianzishwa mwaka 2021 kwa maono ya kutimiza Agizo Kuu kwa kueneza Injili kwa mataifa yote.",
              historyText2: "Tangu wakati huo, tumefanya zaidi ya mikutano 5 ya kuhubiri, tukawafikia maelfu ya watu, na kuanzisha programu za uanafunzi ili kuwatayarisha waumini kwa ajili ya huduma.",
              missionTitle: "Dhamira Yetu",
              missionText: "Tukiongozwa na Agizo Kuu (Mathayo 28:19-20), tumejitolea kuhubiri Injili, kuwafundisha na kuwatayarisha waumini kuishi kwa ujasiri kwa Kristo kupitia mikutano ya kuhubiri, huduma za kijamii na uanafunzi.",
              visionTitle: "Maono Yetu",
              visionText: "Kueneza Injili ya Yesu Kristo kwa mataifa yote na kuwafanya wanafunzi waliojitolea, si waongofu tu.",
              leadershipTitle: "Kutana na Uongozi Wetu",
              leader1Name: "Mchungaji Akilimali",
              leader1Role: "Mwanzilishi & Mchungaji Mkuu",
              leader1Bio: "Mchungaji Akilimali ameongoza NAOGM tangu kuanzishwa kwake, akiwa na shauku kubwa ya kuhubiri Injili na uanafunzi.",
              leader2Name: "Baluka Mercy",
              leader2Role: "Mkurugenzi wa Huduma za Kijamii",
              leader2Bio: "Mercy anasimamia programu za huduma za kijamii za NAOGM, akihakikisha Injili inawafikia jamii za mbali.",
              leader3Name: "Simi",
              leader3Role: "Kiongozi wa Uanafunzi",
              leader3Bio: "Simi amejitolea kuwatayarisha waumini vijana ili waishi imani yao kwa ujasiri.",
              leader4Name: "Tindyebwa Jovia",
              leader4Role: "Kiongozi wa Fedha",
              leader4Bio: "Anasimamia mipango ya kifedha, bajeti na uwajibikaji ndani ya huduma.",
              leader5Name: "Gibemi Musa Lumumba",
              leader5Role: "Kiongozi wa Mawasiliano",
              leader5Bio: "Anaongoza mikakati ya mawasiliano, chapa na shughuli za kuwafikia jamii.",
              faithTitle: "Kauli ya Imani",
              faithScripture: "“Kwa maana siionei haya Injili, kwa sababu ni uweza wa Mungu uletao wokovu kwa kila mtu anayeamini.” – Warumi 1:16",
              faithPoint1: "Tunaamini katika Utatu Mtakatifu: Baba, Mwana na Roho Mtakatifu.",
              faithPoint2: "Tunaamini Biblia ni Neno la Mungu lililovuviwa na lisiloweza kukosea.",
              faithPoint3: "Tunaamini Yesu Kristo ni Mwana wa Mungu, aliyekufa kwa ajili ya dhambi zetu na akafufuka.",
              faithPoint4: "Tunaamini katika Agizo Kuu la kuwafanya wanafunzi wa mataifa yote.",
              ctaTitle: "Wasiliana Nasi",
              ctaButton: "Wasiliana Nasi"
            },
            ministries: {
              seoTitle: "Huduma za NAOGM - Kazi Yetu",
              seoDescription: "Chunguza huduma za Huduma ya Injili ya Kusikatazwa, zikiwemo mikutano ya kuhubiri, uanafunzi, huduma ya vijana na huduma za kijamii.",
              heroTitle: "Huduma Zetu",
              heroSubtitle: "Gundua Jinsi Tunavyohudumu na Kueneza Injili",
              sectionTitle: "Huduma Zetu za Msingi",
              crusadesTitle: "Mikutano ya Kuhubiri Injili",
              crusadesDescription: "Tunaandaa mikutano ya kuhubiri ya kiwango kikubwa ili kushiriki Injili na maelfu, tukileta tumaini na wokovu katika jamii.",
              crusadesLink: "Angalia Mikutano Yanayokuja",
              discipleshipTitle: "Programu za Uanafunzi",
              discipleshipDescription: "Programu zetu zinawapa waumini uwezo wa kukua katika imani, kushiriki Injili na kuishi wito wao kama wanafunzi wa Kristo.",
              discipleshipLink: "Jifunze Zaidi",
              youthTitle: "Huduma ya Vijana",
              youthDescription: "Tunawapa vijana uwezo wa kuishi kwa ujasiri kwa Kristo kupitia ushauri, matukio na uanafunzi maalum kwa vijana.",
              youthLink: "Shiriki",
              outreachTitle: "Huduma za Kijamii",
              outreachDescription: "Tunahudumia jamii kupitia juhudi za kibinadamu, tukishiriki upendo wa Kristo kupitia vitendo vya huduma halisi.",
              outreachLink: "Saidia Huduma Zetu za Kijamii",
              ctaTitle: "Jiunge na Dhamira Yetu",
              ctaButton: "Wasiliana Nasi",
            },
            events: {
              seoTitle: "Matukio ya NAOGM - Mikutano ya Maombi Yanayokuja",
              seoDescription: "Jiunge nasi katika mikutano ya maombi mtandaoni na huduma za kijamii zinazoandaliwa na Huduma ya Injili ya Kusikatazwa.",
              heroTitle: "Matukio Yanayokuja",
              heroSubtitle: "Jiunge Nasi Katika Kueneza Injili na Kuombea Mabadiliko",
              featuredSectionTitle: "Tukio Lililoangaziwa",
              featuredTitle: "Kubadilisha Mifumo ya Maisha – Kipindi cha 2: Kuruka Juu ya Kuta",
              featuredDate: "Jumapili ya Mwisho ya Kila Mwezi, Julai 2025 - Desemba 2025",
              featuredLocation: "Mtandaoni (Google Meet)",
              featuredDescription: "Jiunge nasi katika toleo la pili la mkutano wetu wa maombi mtandaoni, ulioanzishwa Julai 2025. Kupitia maombi ya bidii, ibada na Neno, tunaombea familia, mahusiano, ndoa, kazi na kuokoa roho. Tunaamini Mungu atavunja vizuizi, kuponya majeraha, kurejesha hatima na kutuwezesha kuruka juu ya kila ukuta (Zaburi 18:29). Tarajia ibada inayoongozwa na Roho, mafundisho ya maandiko, maombi yaliyolengwa na faraja ya kinabii.",
              featuredLink: "Jiunge Sasa",
              featuredLinkUrl: "https://meet.google.com/rvi-xaoc-ozj",
              upcomingSectionTitle: "Matukio Mengine Yanayokuja",
              event1Title: "Huduma ya Kijamii 2025",
              event1Date: "Oktoba 26, 2025",
              event1Location: "Mtandaoni (Google Meet)",
              event1Description: "Mkutano wetu wa Virtual Conference 4 umefika. Jiandae na uwalike marafiki na familia ili tuwabariki.",
              event1Link: "Jifunze Zaidi",
              event2Title: "Muhtasari wa Mwaka 2025",
              event2Date: "Januari 2026",
              event2Location: "Goma, DRC",
              event2Description: "Kutana na Kristo, kukua katika imani na kuungana na waumini wengine.",
              event2Link: "Jisajili Sasa",
              ctaTitle: "Ungependa Kuandaa Tukio?",
              ctaButton: "Wasiliana Nasi",
            },
            contact: {
              seoTitle: "Wasiliana na NAOGM",
              seoDescription: "Wasiliana na Huduma ya Injili ya Kusikatazwa kwa maswali, ushirikiano au kujiunga na dhamira yetu.",
              heroTitle: "Wasiliana Nasi",
              heroSubtitle: "Tungependa Kusikia Kutoka Kwako",
              detailsTitle: "Pata Ujumbe",
              detailsSubtitle: "Wasiliana na NAOGM kwa masuala, fursa za ushirikiano au kujiunga na dhamira yetu.",
              addressLabel: "Makao Makuu",
              address: "Goma, North-Kivu (DRC)",
              emailLabel: "Barua Pepe",
              email: "info@notashamedofthegospelministry.org",
              phoneLabel: "Simu",
              phone: "+243-857-274-262",
              mapTitle: "Tupate kwenye Ramani",
            },
            donate: {
              seoTitle: "Changia NAOGM - Saidia Dhamira Yetu",
              seoDescription: "Saidia Huduma ya Injili ya Kusikatazwa kwa kuchangia ili kueneza Injili na kuhudumia jamii ulimwenguni.",
              heroTitle: "Saidia Dhamira Yetu",
              heroSubtitle: "Mchango Wako Unaleta Tofauti",
              sectionTitle: "Njia za Kuchangia",
              sectionSubtitle: "Michango yako ya ukarimu inatusaidia kueneza Injili, kuandaa mikutano ya kuhubiri na kusaidia jamii ulimwenguni.",
              oneTimeTitle: "Mchango wa Mara Moja",
              oneTimeDescription: "Toa mchango wa mara moja ili kusaidia mikutano yetu ya kuhubiri na programu za huduma za kijamii.",
              oneTimeButton: "Changia Sasa",
              monthlyTitle: "Kuchangia Kila Mwezi",
              monthlyDescription: "Kuwa mshirika wa kila mwezi ili kuendeleza programu zetu za uanafunzi na mafunzo.",
              monthlyButton: "Jiunge Kila Mwezi",
              projectTitle: "Ufadhili wa Mradi",
              projectDescription: "Saidia miradi mahususi kama misheni za matibabu au programu za ustawi wa jamii.",
              projectButton: "Fadhili Mradi",
              bankTransferTitle: "Changia Kupitia Uhamisho wa Benki",
              bankLabel: "Benki:",
              bankName: "[Jina la Benki]",
              accountNameLabel: "Jina la Akaunti:",
              accountName: "Huduma ya Injili ya Kusikatazwa",
              accountNumberLabel: "Nambari ya Akaunti:",
              accountNumber: "[Nambari ya Akaunti]",
              swiftCodeLabel: "Msimbo wa Swift:",
              swiftCode: "[Msimbo wa Swift]",
            },
            testimonials: {
              seoTitle: "Ushuhuda wa NAOGM - Hadithi za Athari",
              seoDescription: "Sikiliza hadithi za watu ambao maisha yao yamebadilishwa na huduma za NAOGM.",
              heroTitle: "Ushuhuda",
              heroSubtitle: "Hadithi za Maisha Yaliyobadilishwa",
              sectionTitle: "Watu Wanasema Nini",
              sectionSubtitle: "Sikiliza kutoka kwa wale walioguswa na huduma na juhudi za NAOGM.",
              testimonial1: {
                quote: "Mikutano ya NAOGM ilinikaribisha karibu na Kristo na kubadilisha maisha yangu.",
                author: "John",
                location: "Kampala, Uganda",
              },
              testimonial2: {
                quote: "Programu ya uanafunzi ilinitayarisha kushiriki imani yangu kwa ujasiri.",
                author: "Micheal",
                location: "Nairobi, Kenya",
              },
              testimonial3: {
                quote: "Kambi ya uamsho wa vijana ilikuwa mabadiliko makubwa kwangu. Nilipata kusudi katika Kristo.",
                author: "Okello",
                location: "Dar es Salaam, Tanzania",
              },
              testimonial4: {
                quote: "Kupitia huduma za NAOGM, jamii yangu ilipata tumaini na msaada wa vitendo.",
                author: "Esther",
                location: "Kigali, Rwanda",
              },
              ctaTitle: "Una Hadithi ya Kushiriki?",
              ctaButton: "Wasiliana Nasi",
            },
            prayerRequest: {
              seoTitle: "Ombi la Maombi la NAOGM - Wasilisha Ombi Lako",
              seoDescription: "Wasilisha ombi lako la maombi kwa Huduma ya Injili ya Kusikatazwa, na timu yetu itaombea pamoja nawe.",
              heroTitle: "Ombi la Maombi",
              heroSubtitle: "Tusali Pamoja Nanyi",
              sectionTitle: "Wasilisha Ombi la Maombi",
              sectionSubtitle: "Tunaamini nguvu ya maombi. Shiriki ombi lako, na timu yetu itaombea pamoja nawe.",
              successMessage: "Asante kwa kuwasilisha ombi lako la maombi! Timu yetu itaombea pamoja nawe.",
              form: {
                nameLabel: "Jina",
                namePlaceholder: "Ingiza jina lako",
                nameRequired: "Jina linahitajika",
                emailLabel: "Barua Pepe",
                emailPlaceholder: "Ingiza barua pepe yako",
                emailRequired: "Barua pepe inahitajika",
                emailInvalid: "Tafadhali ingiza barua pepe halali",
                requestLabel: "Ombi la Maombi",
                requestPlaceholder: "Shiriki ombi lako la maombi",
                requestRequired: "Ombi la maombi linahitajika",
                anonymousLabel: "Wasilisha bila kutaja jina",
                submitButton: "Wasilisha Ombi",
                submitting: "Inawasilishwa...",
                errorMessage: "Kulikuwa na hitilafu katika kuwasilisha ombi lako. Tafadhali jaribu tena.",
                privacyNotice: "Data yako itawasilishwa kupitia UseBasin. Angalia",
                privacyLink: "Sera ya Faragha",
              },
            },
            footer: {
              aboutTitle: "Huduma ya Injili ya Kusikatazwa",
              headquartersLabel: "Makao Makuu",
              emailLabel: "Barua Pepe",
              phoneLabel: "Simu",
              linksTitle: "Viungo vya Haraka",
              privacyPolicy: "Sera ya Faragha",
              termsOfService: "Masharti ya Huduma",
              socialTitle: "Ungana Nasi",
              socialText: "Tufuate kwenye mitandao ya kijamii kwa sasisho za mikutano yetu na huduma za kijamii.",
              facebookLabel: "Tembelea ukurasa wetu wa Facebook",
              twitterLabel: "Tufuate kwenye Twitter",
              instagramLabel: "Tufuate kwenye Instagram",
              rightsReserved: "Haki Zote Zimehifadhiwa.",
              poweredBy: "Tovuti inaendeshwa na",
            },
          },
        },
        fr: {
          translation: {
            header: {
              home: "Accueil",
              about: "À Propos",
              ministries: "Ministères",
              events: "Événements",
              donate: "Faire un Don",
              contact: "Contact",
              prayerRequest: "Demande de Prière",
              testimonials: "Témoignages",
            },
            home: {
              seoTitle: "NAOGM - Proclamer l'Évangile dans le Monde",
              seoDescription: "Le Ministère Non Honteux de l'Évangile est dédié à proclamer l'Évangile de Jésus-Christ à toutes les nations à travers des croisades, des actions de proximité et des services communautaires.",
              heroTitle: "Ministère Non Honteux de l'Évangile",
              heroSubtitle: "Proclamer l'Évangile de Jésus-Christ à Toutes les Nations (Romains 1:16)",
              donateButton: "Faire un Don",
              impactTitle: "Notre Impact",
              impactPeople: "Personnes Touchées",
              impactCrusades: "Croisades Organisées",
              impactCountries: "Pays Desservis",
              welcomeTitle: "Bienvenue à NAOGM",
              welcomeText: "Guidés par le Grand Mandat (Matthieu 28:19-20), nous nous engageons à évangéliser, former des disciples et équiper les croyants pour vivre avec audace pour Christ à travers des croisades, des actions de proximité et des services communautaires.",
              visionTitle: "Notre Vision",
              visionText: "Proclamer l'Évangile de Jésus-Christ à toutes les nations et faire des disciples engagés, pas seulement des convertis.",
              welcomeButton: "En Savoir Plus",
              featuredEventTitle: "Événement en Vedette",
              eventTitle: "Changer les Schémas de Vie – Épisode 2 : Sauter par-dessus les Murs",
              eventDetails: "Rejoignez-nous pour une conférence de prière virtuelle chaque dernier dimanche de juillet à décembre 2025, de 15h00 à 18h00 CAT, via Google Meet. Attendez-vous à une adoration puissante, des prières et des enseignements tirés du Psaume 18:29.",
              eventButton: "Rejoindre Maintenant",
              eventLinkUrl: "https://meet.google.com/rvi-xaoc-ozj",
              testimonialsTitle: "Ce Que les Gens Disent",
              testimonial1: "Les croisades de NAOGM m’ont rapproché de Christ et ont transformé ma vie.",
              testimonial2: "Le programme de discipulat m’a équipé pour partager ma foi avec audace.",
              newsletterTitle: "Restez Connecté",
              newsletterText: "Inscrivez-vous à notre newsletter pour recevoir des mises à jour sur nos croisades, actions de proximité, et plus encore.",
              newsletterPlaceholder: "Entrez votre email",
              newsletterButton: "S'abonner",
              newsletterSuccess: "Merci de vous être abonné !",
              newsletterEmailRequired: "L’email est requis",
              newsletterEmailInvalid: "Veuillez entrer une adresse email valide",
              newsletterSubmitting: "Soumission en cours...",
              newsletterError: "Une erreur s’est produite lors de la soumission de votre abonnement. Veuillez réessayer.",
              newsletterPrivacyNotice: "Vos données seront soumises via UseBasin. Consultez leur",
              newsletterPrivacyLink: "Politique de Confidentialité",
              ctaTitle: "Rejoignez Notre Mission",
              ctaEvents: "Événements à Venir",
              ctaDonate: "Soutenez-Nous",
              ctaPrayer: "Soumettre une Demande de Prière",
            },
            about: {
              seoTitle: "À Propos de NAOGM - Notre Mission et Histoire",
              seoDescription: "Découvrez le Ministère Non Honteux de l'Évangile, notre mission, vision, leadership et déclaration de foi.",

              heroTitle: "À Propos de NAOGM",
              heroSubtitle: "Découvrez Notre Mission, Histoire et Leadership",

              historyTitle: "Notre Histoire",
              historyText1: "Le Ministère Non Honteux de l'Évangile (NAOGM) a été fondé en 2021 avec la vision d'accomplir la Grande Commission en proclamant l'Évangile à toutes les nations.",
              historyText2: "Depuis lors, nous avons organisé plus de 5 croisades, touché des milliers de personnes et mis en place des programmes de discipulat pour équiper les croyants pour le ministère.",

              missionTitle: "Notre Mission",
              missionText: "Guidés par la Grande Commission (Matthieu 28:19-20), nous nous engageons à évangéliser, former des disciples et équiper les croyants pour vivre avec audace pour Christ à travers des croisades, des actions de proximité et des services communautaires.",

              visionTitle: "Notre Vision",
              visionText: "Proclamer l'Évangile de Jésus-Christ à toutes les nations et faire des disciples engagés, et non seulement des convertis.",

              leadershipTitle: "Rencontrez Notre Leadership",

              leader1Name: "Pasteur Akilimali",
              leader1Role: "Fondateur & Pasteur Principal",
              leader1Bio: "Le Pasteur Akilimali dirige NAOGM depuis sa création, avec une grande passion pour l'évangélisation et le discipulat.",

              leader2Name: "Baluka Mercy",
              leader2Role: "Directrice des Actions de Proximité",
              leader2Bio: "Mercy supervise les programmes d'actions de proximité de NAOGM, veillant à ce que l'Évangile atteigne les communautés éloignées.",

              leader3Name: "Simi",
              leader3Role: "Responsable du Discipulat",
              leader3Bio: "Simi est dédiée à équiper les jeunes croyants pour qu'ils vivent leur foi avec audace.",

              leader4Name: "Tindyebwa Jovia",
              leader4Role: "Responsable des Finances",
              leader4Bio: "Elle supervise la planification financière, la budgétisation et la reddition de comptes au sein du ministère.",

              leader5Name: "Gibemi Musa Lumumba",
              leader5Role: "Responsable de la Communication",
              leader5Bio: "Il dirige la stratégie de communication, l'image de marque et les initiatives de sensibilisation.",

              faithTitle: "Déclaration de Foi",
              faithScripture: "“Car je n’ai point honte de l’Évangile, car c’est une puissance de Dieu pour le salut de quiconque croit.” – Romains 1:16",

              faithPoint1: "Nous croyons en la Sainte Trinité : Père, Fils et Saint-Esprit.",
              faithPoint2: "Nous croyons que la Bible est la Parole de Dieu inspirée et infaillible.",
              faithPoint3: "Nous croyons que Jésus-Christ est le Fils de Dieu, mort pour nos péchés et ressuscité.",
              faithPoint4: "Nous croyons en la Grande Commission de faire des disciples de toutes les nations.",

              ctaTitle: "Contactez-Nous",
              ctaButton: "Nous Contacter"
            },
            ministries: {
              seoTitle: "Ministères NAOGM - Notre Œuvre",
              seoDescription: "Découvrez les ministères du Ministère Non Honteux de l'Évangile, y compris les croisades, le discipulat, les programmes pour les jeunes et les actions communautaires.",
              heroTitle: "Nos Ministères",
              heroSubtitle: "Découvrez Comment Nous Servons et Proclamons l'Évangile",
              sectionTitle: "Nos Ministères Principaux",
              crusadesTitle: "Croisades",
              crusadesDescription: "Nous organisons de grandes croisades pour partager l'Évangile avec des milliers de personnes, apportant espérance et salut aux communautés.",
              crusadesLink: "Voir les Croisades à Venir",
              discipleshipTitle: "Programmes de Discipulat",
              discipleshipDescription: "Nos programmes permettent aux croyants de grandir dans la foi, de partager l'Évangile et de vivre leur appel en tant que disciples de Christ.",
              discipleshipLink: "En Savoir Plus",
              youthTitle: "Ministère des Jeunes",
              youthDescription: "Nous donnons aux jeunes les moyens de vivre avec audace pour Christ à travers du mentorat, des événements et un discipulat adapté aux jeunes.",
              youthLink: "Participer",
              outreachTitle: "Actions Communautaires",
              outreachDescription: "Nous servons les communautés à travers des efforts humanitaires, partageant l'amour de Christ par des actes concrets de service.",
              outreachLink: "Soutenir Nos Actions",
              ctaTitle: "Rejoignez Notre Mission",
              ctaButton: "Nous Contacter",
            },
            events: {
              seoTitle: "Événements NAOGM - Conférences de Prière à Venir",
              seoDescription: "Rejoignez-nous pour les conférences de prière virtuelles et les actions de proximité organisées par le Ministère Non Honteux de l'Évangile.",
              heroTitle: "Événements à Venir",
              heroSubtitle: "Rejoignez-Nous pour Proclamer l'Évangile et Prier pour la Transformation",
              featuredSectionTitle: "Événement en Vedette",
              featuredTitle: "Changer les Schémas de Vie – Épisode 2 : Sauter par-dessus les Murs",
              featuredDate: "Dernier Dimanche de Chaque Mois, Juillet 2025 - Décembre 2025",
              featuredLocation: "Virtuel (Google Meet)",
              featuredDescription: "Rejoignez-nous pour la deuxième édition de notre conférence de prière virtuelle, lancée en juillet 2025. À travers une prière fervente, l'adoration et la Parole, nous intercédons pour les familles, les relations, les mariages, les carrières et le salut des âmes, croyant que Dieu brisera les barrières, guérira les blessures, restaurera les destinées et nous permettra de sauter par-dessus chaque mur (Psaume 18:29). Attendez-vous à une adoration guidée par l'Esprit, des enseignements scripturaires, des prières ciblées et des encouragements prophétiques.",
              featuredLink: "Rejoindre Maintenant",
              featuredLinkUrl: "https://meet.google.com/rvi-xaoc-ozj",
              upcomingSectionTitle: "Plus d'Événements à Venir",
              event1Title: "Action de Proximité Virtuelle 2025",
              event1Date: "26 Octobre 2025",
              event1Location: "Virtuel (Google Meet)",
              event1Description: "Notre Virtual Conference 4 est arrivée. Préparez-vous et invitez vos amis et familles pour être une bénédiction.",
              event1Link: "En Savoir Plus",
              event2Title: "Bilan de l'année 2025",
              event2Date: "Janvier 2026",
              event2Location: "Goma, RDC",
              event2Description: "Rencontrez Christ, grandissez dans la foi et connectez-vous avec d'autres jeunes croyants.",
              event2Link: "S'inscrire Maintenant",
              ctaTitle: "Voulez-vous Organiser un Événement ?",
              ctaButton: "Nous Contacter",
            },
            contact: {
              seoTitle: "Contactez NAOGM - Prenez Contact",
              seoDescription: "Contactez le Ministère Non Honteux de l'Évangile pour des demandes, partenariats ou pour rejoindre notre mission.",
              heroTitle: "Contactez-Nous",
              heroSubtitle: "Nous Serions Ravis d’Avoir de Vos Nouvelles",
              detailsTitle: "Prenez Contact",
              detailsSubtitle: "Contactez NAOGM pour des demandes, des opportunités de partenariat ou pour rejoindre notre mission.",
              addressLabel: "Siège Social",
              address: "Goma, Nord-Kivu (RDC)",
              emailLabel: "Email",
              email: "info@notashamedofthegospelministry.org",
              phoneLabel: "Téléphone",
              phone: "+243-857-274-262",
              mapTitle: "Trouvez-Nous sur la Carte",
            },
            donate: {
              seoTitle: "Faites un Don à NAOGM - Soutenez Notre Mission",
              seoDescription: "Soutenez le Ministère Non Honteux de l'Évangile en faisant un don pour aider à propager l'Évangile et servir les communautés dans le monde entier.",
              heroTitle: "Soutenez Notre Mission",
              heroSubtitle: "Votre Contribution Fait la Différence",
              sectionTitle: "Moyens de Donner",
              sectionSubtitle: "Vos généreuses contributions nous aident à propager l'Évangile, organiser des croisades et soutenir les communautés dans le monde entier.",
              oneTimeTitle: "Don Unique",
              oneTimeDescription: "Faites un don unique pour soutenir nos croisades évangéliques et programmes d'actions de proximité.",
              oneTimeButton: "Faire un Don Maintenant",
              monthlyTitle: "Don Mensuel",
              monthlyDescription: "Devenez un partenaire mensuel pour soutenir nos programmes de discipulat et de formation.",
              monthlyButton: "Rejoindre Mensuellement",
              projectTitle: "Parrainage de Projet",
              projectDescription: "Soutenez des initiatives spécifiques comme les missions médicales ou les programmes de bien-être communautaire.",
              projectButton: "Parrainer un Projet",
              bankTransferTitle: "Faire un Don par Virement Bancaire",
              bankLabel: "Banque :",
              bankName: "[Nom de la Banque]",
              accountNameLabel: "Nom du Compte :",
              accountName: "Ministère Non Honteux de l'Évangile",
              accountNumberLabel: "Numéro de Compte :",
              accountNumber: "[Numéro de Compte]",
              swiftCodeLabel: "Code Swift :",
              swiftCode: "[Code Swift]",
            },
            testimonials: {
              seoTitle: "Témoignages NAOGM - Histoires d’Impact",
              seoDescription: "Écoutez les personnes dont la vie a été transformée par les efforts du Ministère Non Honteux de l'Évangile.",
              heroTitle: "Témoignages",
              heroSubtitle: "Histoires de Vies Transformées",
              sectionTitle: "Ce Que les Gens Disent",
              sectionSubtitle: "Écoutez ceux qui ont été touchés par le ministère et les efforts de NAOGM.",
              testimonial1: {
                quote: "Les croisades de NAOGM m’ont rapproché de Christ et ont transformé ma vie.",
                author: "John",
                location: "Kampala, Ouganda",
              },
              testimonial2: {
                quote: "Le programme de discipulat m’a équipé pour partager ma foi avec audace.",
                author: "Micheal",
                location: "Nairobi, Kenya",
              },
              testimonial3: {
                quote: "Le camp de réveil pour jeunes a été un tournant pour moi. J’ai trouvé un but en Christ.",
                author: "Okello",
                location: "Dar es Salaam, Tanzanie",
              },
              testimonial4: {
                quote: "Grâce aux actions de proximité de NAOGM, ma communauté a reçu de l’espoir et un soutien concret.",
                author: "Esther",
                location: "Kigali, Rwanda",
              },
              ctaTitle: "Avez-vous une histoire à partager ?",
              ctaButton: "Nous Contacter",
            },
            prayerRequest: {
              seoTitle: "Demande de Prière NAOGM - Soumettez Votre Prière",
              seoDescription: "Soumettez votre demande de prière au Ministère Non Honteux de l’Évangile, et notre équipe priera avec vous.",
              heroTitle: "Demande de Prière",
              heroSubtitle: "Prions Ensemble",
              sectionTitle: "Soumettez une Demande de Prière",
              sectionSubtitle: "Nous croyons en la puissance de la prière. Partagez votre demande de prière, et notre équipe priera pour vous.",
              successMessage: "Merci d’avoir soumis votre demande de prière ! Notre équipe priera avec vous.",
              form: {
                nameLabel: "Nom",
                namePlaceholder: "Entrez votre nom",
                nameRequired: "Le nom est requis",
                emailLabel: "Email",
                emailPlaceholder: "Entrez votre email",
                emailRequired: "L’email est requis",
                emailInvalid: "Veuillez entrer une adresse email valide",
                requestLabel: "Demande de Prière",
                requestPlaceholder: "Partagez votre demande de prière",
                requestRequired: "La demande de prière est requise",
                anonymousLabel: "Soumettre anonymement",
                submitButton: "Soumettre la Demande",
                submitting: "Soumission en cours...",
                errorMessage: "Une erreur s’est produite lors de la soumission de votre demande. Veuillez réessayer.",
                privacyNotice: "Vos données seront soumises via UseBasin. Consultez leur",
                privacyLink: "Politique de Confidentialité",
              },
            },
            footer: {
              aboutTitle: "Ministère Non Honteux de l'Évangile",
              headquartersLabel: "Siège Social",
              emailLabel: "Email",
              phoneLabel: "Téléphone",
              linksTitle: "Liens Rapides",
              privacyPolicy: "Politique de Confidentialité",
              termsOfService: "Conditions de Service",
              socialTitle: "Connectez-vous avec Nous",
              socialText: "Suivez-nous sur les réseaux sociaux pour des mises à jour sur nos croisades et actions de proximité.",
              facebookLabel: "Visitez notre page Facebook",
              twitterLabel: "Suivez-nous sur Twitter",
              instagramLabel: "Suivez-nous sur Instagram",
              rightsReserved: "Tous Droits Réservés.",
              poweredBy: "Site créé par",
            },
          },
        },
      },
      lng: 'en',
      fallbackLng: 'en',
      preload: ['en', 'sw', 'fr'],
      ns: ['translation'],
      defaultNS: 'translation',
      interpolation: {
        escapeValue: false,
      },
      detection: {
        order: ['navigator', 'htmlTag', 'path', 'subpath'],
        caches: ['localStorage', 'cookie'],
      },
    },
    (err) => {
      if (err) console.error('Failed to initialize i18n:', err);
    }
  );

i18n.on('languageChanged', (lng) => {
  const validLang = ['en', 'sw', 'fr'].includes(lng) ? lng : 'en';
  document.documentElement.setAttribute('lang', validLang);
  document.documentElement.setAttribute('dir', 'ltr');
});

document.documentElement.setAttribute('lang', ['en', 'sw', 'fr'].includes(i18n.language) ? i18n.language : 'en');
document.documentElement.setAttribute('dir', 'ltr');

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <StrictMode>
    <ErrorBoundary>
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-secondary text-textPrimary">Loading...</div>}>
        <App />
      </Suspense>
    </ErrorBoundary>
  </StrictMode>,
);